'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePermissions } from '@/hooks/usePermissions'
import { notifyGrantsChanged } from '@/lib/auth/grantsChanged'
import { USERS_PERMISSIONS } from '@/lib/permissions/users'
import {
  type AdminUser,
  deactivateUser, getUser, hardDeleteUser, listUsers, registerUser, resetUserPassword,
  setUserChannels, updateUser, assignSubRole, unassignSubRole,
  type UpdateUserPayload,
} from '@/services/admin/users'

export const adminUsersKeys = {
  all: ['admin', 'users'] as const,
  lists: () => [...adminUsersKeys.all, 'list'] as const,
  detail: (id: string) => [...adminUsersKeys.all, 'detail', id] as const,
}

/** Referência única do vazio: `users` é dep de `useMemo` nos consumidores (`nameById` da auditoria). */
const NENHUM: AdminUser[] = []

export function useAdminUsers() {
  const qc = useQueryClient()
  const invalidateList = () => qc.invalidateQueries({ queryKey: adminUsersKeys.lists() })

  /* `GET /users` é o grant de GESTÃO (`users:read`, fora de todo preset): sem ele o back dá 403. O gate vale no
     `enabled` (o request não sai) E na saída — `enabled: false` não limpa o cache, então um grant revogado
     continuaria mostrando a lista de antes. Revalidando = o grant ainda não foi confirmado: não busca, carregando. */
  const { isRevalidating, hasPermission } = usePermissions()
  const podeListar = !isRevalidating && hasPermission(USERS_PERMISSIONS.READ)
  const list = useQuery({ queryKey: adminUsersKeys.lists(), queryFn: listUsers, enabled: podeListar })
  const create = useMutation({ mutationFn: registerUser, onSuccess: invalidateList })
  const update = useMutation({
    mutationFn: ({ id, ...payload }: UpdateUserPayload & { id: string }) => updateUser(id, payload),
    onSuccess: (_, { id }) => {
      invalidateList()
      qc.invalidateQueries({ queryKey: adminUsersKeys.detail(id) })
      notifyGrantsChanged() // role pode ter mudado (inclusive o próprio)
    },
  })
  const deactivate = useMutation({
    mutationFn: deactivateUser,
    onSuccess: (_, id) => {
      invalidateList()
      qc.invalidateQueries({ queryKey: adminUsersKeys.detail(id) })
      notifyGrantsChanged()
    },
  })
  const hardDelete = useMutation({
    mutationFn: hardDeleteUser,
    onSuccess: (_, id) => {
      invalidateList()
      // O user não existe mais: remove (não invalida) pra não disparar refetch que só daria 404.
      qc.removeQueries({ queryKey: adminUsersKeys.detail(id) })
    },
  })
  const resetPassword = useMutation({ mutationFn: resetUserPassword })

  return {
    users: podeListar ? (list.data ?? NENHUM) : NENHUM,
    isLoading: isRevalidating || (podeListar && list.isPending),
    isError: podeListar && list.isError,
    refetch: list.refetch,
    create,
    update,
    deactivate,
    hardDelete,
    resetPassword,
  }
}

export function useAdminUserDetail(id: string) {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: adminUsersKeys.detail(id) })
    qc.invalidateQueries({ queryKey: adminUsersKeys.lists() })
    notifyGrantsChanged() // todas as mutations deste hook mexem em grants/escopos
  }
  const detail = useQuery({ queryKey: adminUsersKeys.detail(id), queryFn: () => getUser(id), enabled: !!id })
  const update = useMutation({ mutationFn: (payload: UpdateUserPayload) => updateUser(id, payload), onSuccess: invalidate })
  const setChannels = useMutation({ mutationFn: (channelIds: string[]) => setUserChannels(id, channelIds), onSuccess: invalidate })
  const assign = useMutation({ mutationFn: (key: string) => assignSubRole(key, id), onSuccess: invalidate })
  const unassign = useMutation({ mutationFn: (key: string) => unassignSubRole(key, id), onSuccess: invalidate })
  return { user: detail.data ?? null, isLoading: detail.isPending, update, setChannels, assign, unassign }
}
